import { t } from '@lingui/macro';
import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';

import { useGetLimitsUsage } from '@components/bulk_management/user-settings/PlanLimits';

import UsageInfoCard, { InfoCard, Usage } from './UsageInfoCard';
import { CustomRoleUsageCard } from '@components/bulk_management/user-settings';

interface UsageInfoCardWrapperProps {
  adminCard: InfoCard;
  collabCard: InfoCard;
  resourceType: string;
  resourceKey: string;
}

const UsageInfoCardWrapper: FunctionComponent<UsageInfoCardWrapperProps> = ({
  adminCard,
  collabCard,
  resourceType,
  resourceKey
}) => {
  const customPermissionsFeatureOn = BFG.hasFeature('custom_permissions_rollout');
  const [adminCardData, setAdminCardData] = useState<InfoCard>(adminCard);
  const [collabCardData, setCollabCardData] = useState<InfoCard>(collabCard);
  const { adminLimit, collabLimit, fetchAdminLimits, fetchCollabLimits } = useGetLimitsUsage(
    BFG.organization_key,
    resourceType
  );

  const loadLimits = useCallback((): void => {
    fetchAdminLimits();
    fetchCollabLimits();
  }, [fetchAdminLimits, fetchCollabLimits]);

  useEffect(() => {
    loadLimits();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createWarningFlag = (info: Usage): string => {
    if (typeof info.limit === 'string' || info.limit === -1) {
      return '';
    }
    return info.limit - info.current === (info.invites || 0) + 1 && t`1 left`;
  };

  const createDangerFlag = (info: Usage): string => {
    if (typeof info.limit === 'string' || info.limit === -1) {
      return '';
    }
    return (info.current >= info.limit || (info.invites || 0) >= info.limit - info.current) && t`0 left`;
  };

  useEffect(() => {
    const collabUsage = {
      ...collabCardData.usage,
      limit: collabLimit?.data?.find((bf) => resourceKey === bf.relationships.resource.data.id)?.attributes.value || 0
    };
    const adminUsage = {
      ...adminCardData.usage,
      limit: adminLimit?.data?.find((bf) => resourceKey === bf.relationships.resource.data.id)?.attributes.value || 0
    };
    setAdminCardData({
      ...adminCardData,
      warningAlert: createWarningFlag(adminUsage),
      dangerAlert: createDangerFlag(adminUsage),
      usage: adminUsage
    });
    setCollabCardData({
      ...collabCardData,
      warningAlert: createWarningFlag(collabUsage),
      dangerAlert: createDangerFlag(collabUsage),
      usage: collabUsage
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceKey, adminLimit?.data, collabLimit?.data, resourceType]);
  const cardContainerStyle = {
    flex: '1 1 0',
    order: 1,
    maxWidth: '50%'
  };

  return (
    <div className="usage-info-card-wrapper" style={{
      display: 'flex',
      flexWrap: 'wrap',
      padding: '0 0 46px',
      gap: '24px'
    }}>
      <div className="admin-card-container" style={cardContainerStyle}>
        <UsageInfoCard {...adminCardData} setLimitsAction={loadLimits} />
      </div>
      <div className="collab-card-container" style={{ ...cardContainerStyle, order: 3 }}>
        <UsageInfoCard {...collabCardData} setLimitsAction={loadLimits} />
      </div>

      {customPermissionsFeatureOn && <CustomRoleUsageCard
        className="custom-role-card-container"
        hasSetLimits
        organizationKey={BFG.organization_key}
        resourceType={resourceType}
        resourceKey={resourceKey}
        style={{ ...cardContainerStyle, height: 'auto', maxWidth: '33%' }}
        viewOnly={false}
      />}
    </div>
  );
};

export default UsageInfoCardWrapper;
